/**
 * POS Main Page - Order entry and cart management
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Search, Plus, Minus, Trash2, CreditCard, Banknote, 
  Gift, User, Percent, MoreVertical, ChevronRight, 
  Smartphone, DollarSign, Star, Bitcoin, SplitSquareHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import PaymentDialog from '../components/PaymentDialog';
import GiftCardSellModal from '../components/GiftCardSellModal';
import GiftCardBalanceCheckModal from '../components/GiftCardBalanceCheckModal';
import { cartMoney, checkoutLines } from '../lib/cartMoney';
import { Link } from 'react-router-dom';
import { api as diningApi } from '@/apps/dining/lib/api';
import { posPath } from '../posPath';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function POSPage() {
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [paymentDialog, setPaymentDialog] = useState({ open: false, method: null, orderId: null });
  const [balanceDue, setBalanceDue] = useState([]); // register orders left part-paid (split payment)
  const [showBalanceDue, setShowBalanceDue] = useState(false);
  const [showSellGiftCard, setShowSellGiftCard] = useState(false);
  const [showBalanceCheck, setShowBalanceCheck] = useState(false);
  const [mobileTab, setMobileTab] = useState('menu'); // 'menu' or 'cart'
  const { getToken } = useAuth();
  const { 
    cart, addItem, updateQuantity, removeItem, clearCart,
    getItemCount,
    orderType, setOrderType, customerName, setCustomerName
  } = useCart();

  const [taxRate, setTaxRate] = useState(null); // percent, from the restaurant's checkout settings

  // The cart shows what the server will charge: POST /api/checkout/price (the same
  // pricing.price_items the order is created with). Until it answers for THIS cart, the
  // same half-up rounding in cents (lib/cartMoney) - never a float sum.
  const lines = useMemo(() => checkoutLines(cart), [cart]);
  const cartKey = useMemo(() => JSON.stringify(lines), [lines]);
  const [quote, setQuote] = useState(null); // { key, subtotal_cents, tax_cents, total_cents, tax_rate_percent }
  const local = useMemo(() => cartMoney(cart, taxRate), [cart, taxRate]);
  const priced = quote && quote.key === cartKey ? quote : null;
  const money = priced || local;
  const shownRate = priced ? priced.tax_rate_percent : taxRate;
  const taxKnown = priced ? true : taxRate != null;

  useEffect(() => {
    if (!lines.length || taxRate == null) { setQuote(null); return undefined; }
    let stale = false;
    const timer = setTimeout(() => {
      axios.post(`${API}/api/checkout/price`, { items: lines }, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(res => { if (!stale) setQuote({ ...res.data, key: cartKey }); })
        .catch(() => { if (!stale) setQuote(null); });
    }, 250);
    return () => { stale = true; clearTimeout(timer); };
  }, [cartKey, lines, taxRate, getToken]);

  const fetchMenu = useCallback(async () => {
    try {
      // the ONE menu read (GET /api/dining/menu: active categories, display prices
      // in cents); sold-out items are left off the register's grid
      const response = await axios.get(`${API}/api/dining/menu`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const categories = (response.data?.categories || []).map(cat => ({
        id: cat.id,
        name: cat.name,
        items: (cat.items || []).filter(item => item.available).map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          price: (item.price_cents || 0) / 100,
          category: cat.name,
          category_id: cat.id,
          image_url: item.image_url,
          is_available: item.available,
        })),
      })).filter(cat => cat.items.length > 0);
      const items = categories.flatMap(cat => cat.items);
      setMenu({ categories, items });
      
      if (categories.length > 0) {
        setSelectedCategory(categories[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchMenu();
    axios.get(`${API}/api/checkout/settings`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(res => setTaxRate(res.data.tax_rate_percent))
      .catch(() => setTaxRate(null));
  }, [fetchMenu, getToken]);

  const handleAddToCart = (item) => {
    addItem(item, [], 1, '');
    toast.success(`Added ${item.name}`);
  };

  const currentItems = selectedCategory
    ? menu.categories.find(c => c.id === selectedCategory)?.items || []
    : menu.items;

  const filteredItems = searchQuery
    ? currentItems.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentItems;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handlePay = (method) => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    // Open payment dialog with display integration
    setPaymentDialog({ open: true, method, orderId: null });
  };

  const loadBalanceDue = useCallback(() => {
    axios.get(`${API}/api/checkout/orders/balance-due`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(res => setBalanceDue(res.data?.orders || []))
      .catch(() => setBalanceDue([]));
  }, [getToken]);

  useEffect(() => { loadBalanceDue(); }, [loadBalanceDue]);

  // Cash drawer of this register: a reminder to open it before taking cash (hidden for roles without drawers)
  const [drawerOpen, setDrawerOpen] = useState(null);
  useEffect(() => {
    let stale = false;
    diningApi.get('/cash-drawers/current')
      .then((d) => { if (!stale) setDrawerOpen(!!d.drawer); })
      .catch(() => { if (!stale) setDrawerOpen(null); });
    return () => { stale = true; };
  }, []);

  const resumeOrder = (orderId) => {
    setShowBalanceDue(false);
    setPaymentDialog({ open: true, method: 'split', orderId });
  };

  const handlePaymentSuccess = () => {
    // A resumed part-paid order is not this cart's
    if (!paymentDialog.orderId) clearCart();
    loadBalanceDue();
  };

  const handlePaymentClose = () => {
    setPaymentDialog({ open: false, method: null, orderId: null });
    loadBalanceDue();
  };

  // Prepare order data for payment dialog
  const orderData = {
    items: cart,
    customerName,
    orderType,
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Payment Dialog with Display Integration */}
      <PaymentDialog
        open={paymentDialog.open}
        onClose={handlePaymentClose}
        paymentMethod={paymentDialog.method}
        orderData={paymentDialog.orderId ? null : orderData}
        orderId={paymentDialog.orderId}
        onSuccess={handlePaymentSuccess}
      />

      {drawerOpen === false && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-200 text-sm px-4 py-2 flex items-center gap-3" data-testid="pos-no-drawer">
          <span className="flex-1">No cash drawer is open on this register. Open it before taking cash.</span>
          <Link to={posPath('drawer')} className="font-semibold underline">Open drawer</Link>
        </div>
      )}



      {/* Mobile: Tab navigation for Menu/Cart */}
      <div className="lg:hidden flex border-b border-slate-800 bg-slate-900">
        <button
          onClick={() => setMobileTab('menu')}
          className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
            mobileTab === 'menu' 
              ? 'text-emerald-400 border-b-2 border-emerald-400 bg-slate-800' 
              : 'text-slate-400'
          }`}
        >
          Menu
        </button>
        <button
          onClick={() => setMobileTab('cart')}
          className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
            mobileTab === 'cart' 
              ? 'text-emerald-400 border-b-2 border-emerald-400 bg-slate-800' 
              : 'text-slate-400'
          }`}
        >
          Cart ({cart.length}) - {formatCurrency(money.total_cents / 100)}
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Panel - Menu */}
        <div className={`flex-1 flex flex-col bg-slate-950 min-h-0 ${mobileTab === 'cart' ? 'hidden lg:flex' : ''}`}>
          {/* Search Bar */}
          <div className="p-3 sm:p-4 border-b border-slate-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
              <Input
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 sm:pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-9 sm:h-10 text-sm"
              />
            </div>
          </div>

          {/* Categories - Horizontal scroll */}
          <div className="border-b border-slate-800">
            <ScrollArea className="w-full">
              <div className="flex gap-2 p-3 sm:p-4">
                {menu.categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    size="sm"
                    className={`flex-shrink-0 h-8 sm:h-9 text-xs sm:text-sm ${selectedCategory === category.id 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                    }`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name}
                    <Badge variant="secondary" className="ml-1.5 sm:ml-2 bg-slate-700 text-[10px] sm:text-xs px-1.5">
                      {category.items?.length || 0}
                    </Badge>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Menu Items Grid - Responsive columns */}
          <ScrollArea className="flex-1 p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
              {filteredItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleAddToCart(item)}
                  className="bg-slate-800 rounded-lg sm:rounded-xl p-2 sm:p-4 text-left hover:bg-slate-700 transition-colors border border-slate-700 hover:border-emerald-500"
                  data-testid={`menu-item-${item.id}`}
                >
                  <div className="aspect-video bg-slate-700 rounded-lg mb-2 sm:mb-3 overflow-hidden flex items-center justify-center text-2xl sm:text-3xl">
                    {item.image_url ? (
                      <img src={item.image_url} alt="" loading="lazy" className="h-full w-full object-cover"
                           onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : '🍽️'}
                  </div>
                  <h3 className="font-semibold text-white mb-0.5 sm:mb-1 line-clamp-2 leading-snug text-xs sm:text-base" title={item.name}>{item.name}</h3>
                  <p className="text-emerald-400 font-bold text-sm sm:text-base">{formatCurrency(item.price)}</p>
                </button>
              ))}
            </div>

            {filteredItems.length === 0 && (
              <div className="text-center py-8 sm:py-12 text-slate-500">
                <p className="text-sm sm:text-base">No items found</p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right Panel - Cart */}
        <div className={`w-full lg:w-96 bg-slate-900 lg:border-l border-slate-800 flex flex-col ${mobileTab === 'menu' ? 'hidden lg:flex' : ''}`}>
          {/* Order Type */}
          <div className="p-3 sm:p-4 border-b border-slate-800">
            <div className="flex gap-1.5 sm:gap-2">
              {['dine_in', 'takeout', 'delivery'].map((type) => (
                <Button
                  key={type}
                  variant={orderType === type ? 'default' : 'outline'}
                  size="sm"
                  className={`flex-1 h-8 sm:h-9 text-[10px] sm:text-sm ${orderType === type 
                    ? 'bg-emerald-600' 
                    : 'bg-slate-800 border-slate-700'
                  }`}
                  onClick={() => setOrderType(type)}
                >
                  {type === 'dine_in' ? 'Dine In' : type === 'takeout' ? 'Takeout' : 'Delivery'}
                </Button>
              ))}
            </div>
          </div>

          {/* Customer Name */}
          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-800">
            <Input
              placeholder="Customer name (optional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm"
            />
          </div>

          {/* Cart Items */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
              {cart.length === 0 ? (
                <div className="text-center py-6 sm:py-12 text-slate-500">
                  <p className="text-sm">Cart is empty</p>
                  <p className="text-xs mt-1">Tap items to add them</p>
                </div>
              ) : (
                cart.map((item) => (
                  <div 
                    key={item.id}
                    className="bg-slate-800 rounded-lg p-2 sm:p-3"
                  >
                    <div className="flex justify-between items-start mb-1.5 sm:mb-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate text-xs sm:text-sm">{item.name}</h4>
                        <p className="text-[10px] sm:text-sm text-slate-400">
                          {formatCurrency(item.price + item.modifierTotal)}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 sm:h-8 sm:w-8 text-slate-500 hover:text-red-400"
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-6 w-6 sm:h-8 sm:w-8 bg-slate-700 border-slate-600"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                      <span className="w-6 sm:w-8 text-center text-white font-medium text-xs sm:text-sm">{item.quantity}</span>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-6 w-6 sm:h-8 sm:w-8 bg-slate-700 border-slate-600"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                      <div className="flex-1 text-right">
                        <span className="text-white font-semibold text-xs sm:text-sm">
                          {formatCurrency((item.price + item.modifierTotal) * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Cart Summary - Compact on mobile */}
          <div className="p-3 sm:p-4 border-t border-slate-800 space-y-1.5 sm:space-y-3"
            data-testid="cart-summary" data-priced={priced ? 'server' : 'local'}>
            <div className="flex justify-between text-slate-400 text-xs sm:text-sm">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatCurrency(money.subtotal_cents / 100)}</span>
            </div>
            <div className="flex justify-between text-slate-400 text-xs sm:text-sm">
              <span>Tax{taxKnown ? ` (${shownRate}%)` : ' (not set)'}</span>
              <span data-testid="cart-tax">{taxKnown ? formatCurrency((money.tax_cents || 0) / 100) : '—'}</span>
            </div>
            <div className="flex justify-between text-base sm:text-xl font-bold text-white pt-1.5 sm:pt-2 border-t border-slate-700">
              <span>Total</span>
              <span data-testid="cart-total">{formatCurrency(money.total_cents / 100)}</span>
            </div>
          </div>

          {/* Payment Buttons */}
          <div className="p-2.5 sm:p-3 border-t border-slate-800 space-y-2">
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {[
                { id: 'cash',    label: 'Cash',    icon: Banknote,   cls: 'bg-green-600 hover:bg-green-700' },
                { id: 'card',    label: 'Card',    icon: CreditCard, cls: 'bg-blue-600 hover:bg-blue-700' },
                { id: 'zelle',   label: 'Zelle',   icon: DollarSign, cls: 'bg-violet-600 hover:bg-violet-700' },
                { id: 'venmo',   label: 'Venmo',   icon: Smartphone, cls: 'bg-cyan-600 hover:bg-cyan-700' },
                { id: 'gift',    label: 'Gift Card', icon: Gift,     cls: 'bg-amber-600 hover:bg-amber-700' },
                { id: 'loyalty', label: 'Loyalty', icon: Star,       cls: 'bg-pink-600 hover:bg-pink-700' },
                { id: 'crypto',  label: 'Crypto',  icon: Bitcoin,    cls: 'bg-orange-600 hover:bg-orange-700' },
                { id: 'split',   label: 'Split',   icon: SplitSquareHorizontal, cls: 'bg-sky-700 hover:bg-sky-800' },
              ].map(btn => {
                const Icon = btn.icon;
                return (
                  <Button
                    key={btn.id}
                    className={`h-9 sm:h-11 text-[10px] sm:text-xs px-1 sm:px-2 flex flex-col items-center justify-center gap-0.5 ${btn.cls}`}
                    onClick={() => handlePay(btn.id)}
                    disabled={cart.length === 0}
                    data-testid={`pay-${btn.id}-btn`}
                  >
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {btn.label}
                  </Button>
                );
              })}
            </div>
            {balanceDue.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10">
                <button type="button" onClick={() => setShowBalanceDue(v => !v)} data-testid="balance-due-btn"
                  className="w-full h-10 px-3 flex items-center justify-between text-amber-300 text-xs sm:text-sm font-semibold">
                  <span>Balance due · {balanceDue.length} order{balanceDue.length > 1 ? 's' : ''}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showBalanceDue ? 'rotate-90' : ''}`} />
                </button>
                {showBalanceDue && (
                  <div className="border-t border-amber-500/30 divide-y divide-amber-500/20">
                    {balanceDue.map(o => (
                      <button key={o.order_id} type="button" onClick={() => resumeOrder(o.order_id)} data-testid={`balance-due-${o.order_id}`}
                        className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-amber-500/10">
                        <span className="text-white text-xs sm:text-sm">Order #{o.order_id}<span className="block text-[11px] text-slate-400">{formatCurrency(o.paid_cents / 100)} paid</span></span>
                        <span className="text-amber-300 text-sm font-bold">{formatCurrency(o.due_cents / 100)} due</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
              <Button
                variant="outline"
                className="h-9 sm:h-11 border-slate-700 text-slate-400 text-xs sm:text-sm"
                onClick={clearCart}
                disabled={cart.length === 0}
                data-testid="clear-cart-btn"
              >
                Clear Cart
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
              <Button
                className="h-9 sm:h-11 text-xs sm:text-sm bg-amber-600/80 hover:bg-amber-700"
                onClick={() => setShowSellGiftCard(true)}
                data-testid="sell-giftcard-btn"
              >
                <Gift className="w-4 h-4 mr-1.5" />
                Sell Gift Card
              </Button>
              <Button
                variant="outline"
                className="h-9 sm:h-11 border-slate-700 text-cyan-400 text-xs sm:text-sm hover:bg-cyan-500/10"
                onClick={() => setShowBalanceCheck(true)}
                data-testid="check-balance-btn"
              >
                <CreditCard className="w-4 h-4 mr-1.5" />
                Check Balance
              </Button>
            </div>
          </div>
        </div>
      </div>

      <GiftCardSellModal
        open={showSellGiftCard}
        onClose={() => setShowSellGiftCard(false)}
      />
      <GiftCardBalanceCheckModal
        open={showBalanceCheck}
        onClose={() => setShowBalanceCheck(false)}
      />
    </div>
  );
}
